/**
 * WHAT: Collection of hooks for attaching DOM event listeners with automatic
 *       cleanup on unmount (useEventListener, useWindowEvent, useDocumentEvent,
 *       useKeyPress, useClickOutside, useOnlineStatus, useVisibilityChange).
 * WHY:  Forgetting to remove event listeners is a common source of memory leaks
 *       in React. These hooks guarantee cleanup via useEffect return functions,
 *       and they store handlers in refs so listener re-attachment only happens
 *       when the target element or event name changes, not on every render.
 */

import { useEffect, useRef } from 'react';

/**
 * WHAT: Attaches a single event listener to any DOM element (or window/document)
 *       and removes it automatically when the component unmounts.
 * WHY:  Provides a universal, leak-proof primitive that all other event hooks
 *       in this file delegate to, keeping cleanup logic in one place.
 *
 * @param {string} eventName - The name of the event to listen for
 * @param {Function} handler - The event handler function
 * @param {Element|Window|Document} element - The element to attach the listener to (default: window)
 * @param {Object} options - Event listener options (capture, passive, once)
 *
 * @example
 * // Window resize event
 * useEventListener('resize', () => {
 *   setWindowWidth(window.innerWidth);
 * });
 *
 * // Document click event
 * useEventListener('click', handleDocumentClick, document);
 *
 * // Element-specific event with ref
 * const buttonRef = useRef(null);
 * useEventListener('click', handleClick, buttonRef.current);
 *
 * // With options
 * useEventListener('scroll', handleScroll, window, { passive: true });
 */
export function useEventListener(eventName, handler, element = window, options = {}) {
  // Store handler in ref to avoid re-attaching on every render
  const savedHandler = useRef(handler);

  // Update ref when handler changes
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // Make sure element supports addEventListener
    const targetElement = element?.current ?? element;
    if (!targetElement?.addEventListener) {
      return;
    }

    // Create event listener that calls handler from ref
    const eventListener = (event) => savedHandler.current(event);

    targetElement.addEventListener(eventName, eventListener, options);

    // Cleanup on unmount
    return () => {
      targetElement.removeEventListener(eventName, eventListener, options);
    };
  }, [eventName, element, options]);
}

/**
 * WHAT: Shorthand for useEventListener bound to the window object.
 * WHY:  Window-level events (resize, online/offline, beforeunload) are very
 *       common in DAMA; this removes the need to pass `window` explicitly.
 *
 * useWindowEvent - Convenience hook for window events
 *
 * @param {string} eventName - The name of the window event
 * @param {Function} handler - The event handler function
 * @param {Object} options - Event listener options
 *
 * @example
 * useWindowEvent('resize', () => {
 *   setDimensions({ width: window.innerWidth, height: window.innerHeight });
 * });
 *
 * useWindowEvent('online', () => setIsOnline(true));
 * useWindowEvent('offline', () => setIsOnline(false));
 */
export function useWindowEvent(eventName, handler, options = {}) {
  useEventListener(eventName, handler, window, options);
}

/**
 * WHAT: Shorthand for useEventListener bound to the document object.
 * WHY:  Document-level events (keydown, visibilitychange, click-outside
 *       detection) are used throughout DAMA modals and dropdowns.
 *
 * useDocumentEvent - Convenience hook for document events
 *
 * @param {string} eventName - The name of the document event
 * @param {Function} handler - The event handler function
 * @param {Object} options - Event listener options
 *
 * @example
 * // Close dropdown when clicking outside
 * useDocumentEvent('click', (e) => {
 *   if (!dropdownRef.current?.contains(e.target)) {
 *     setIsOpen(false);
 *   }
 * });
 *
 * // Handle keyboard shortcuts
 * useDocumentEvent('keydown', (e) => {
 *   if (e.key === 'Escape') {
 *     closeModal();
 *   }
 * });
 */
export function useDocumentEvent(eventName, handler, options = {}) {
  useEventListener(eventName, handler, document, options);
}

/**
 * WHAT: Fires a handler when a specific key (with optional modifier keys) is pressed.
 * WHY:  DAMA uses keyboard shortcuts (Escape to close modals, Ctrl+S to save)
 *       and this hook provides a declarative, modifier-aware API for them.
 *
 * useKeyPress - Hook for handling specific key presses
 *
 * @param {string|string[]} targetKey - The key(s) to listen for
 * @param {Function} handler - The handler to call when key is pressed
 * @param {Object} options - Additional options
 * @param {boolean} options.ctrl - Require Ctrl key
 * @param {boolean} options.shift - Require Shift key
 * @param {boolean} options.alt - Require Alt key
 * @param {boolean} options.meta - Require Meta key (Cmd on Mac)
 *
 * @example
 * // Single key
 * useKeyPress('Escape', () => closeModal());
 *
 * // Multiple keys
 * useKeyPress(['Enter', 'Space'], () => submitForm());
 *
 * // With modifiers
 * useKeyPress('s', () => saveDocument(), { ctrl: true });
 */
export function useKeyPress(targetKey, handler, options = {}) {
  const { ctrl = false, shift = false, alt = false, meta = false } = options;

  useDocumentEvent('keydown', (event) => {
    const keys = Array.isArray(targetKey) ? targetKey : [targetKey];

    // Check if pressed key matches any target key
    const keyMatches = keys.some(key =>
      event.key === key || event.code === key
    );

    // Check modifier keys if specified
    const modifiersMatch =
      (!ctrl || event.ctrlKey) &&
      (!shift || event.shiftKey) &&
      (!alt || event.altKey) &&
      (!meta || event.metaKey);

    // Only require exact modifier match if any modifier is specified
    const hasModifierRequirement = ctrl || shift || alt || meta;
    const strictModifierMatch = !hasModifierRequirement || (
      event.ctrlKey === ctrl &&
      event.shiftKey === shift &&
      event.altKey === alt &&
      event.metaKey === meta
    );

    if (keyMatches && modifiersMatch && strictModifierMatch) {
      handler(event);
    }
  });
}

/**
 * WHAT: Calls a handler when the user clicks anywhere outside the referenced element.
 * WHY:  Dropdowns, popovers, and inline-edit cells in DAMA need to close when
 *       the user clicks elsewhere; this hook encapsulates that pattern.
 *
 * useClickOutside - Hook for detecting clicks outside a referenced element
 *
 * @param {React.RefObject} ref - Ref to the element to detect outside clicks for
 * @param {Function} handler - Handler to call when clicking outside
 *
 * @example
 * const dropdownRef = useRef(null);
 * useClickOutside(dropdownRef, () => setIsOpen(false));
 *
 * return (
 *   <div ref={dropdownRef}>
 *     {isOpen && <DropdownMenu />}
 *   </div>
 * );
 */
export function useClickOutside(ref, handler) {
  useDocumentEvent('mousedown', (event) => {
    // Do nothing if clicking ref's element or its descendants
    if (!ref.current || ref.current.contains(event.target)) {
      return;
    }
    handler(event);
  });
}

/**
 * WHAT: Returns a boolean indicating whether the browser currently has network connectivity.
 * WHY:  DAMA can show an offline banner or pause polling when the user loses
 *       connectivity, preventing confusing API errors.
 *
 * useOnlineStatus - Hook that tracks online/offline status
 *
 * @returns {boolean} Whether the browser is online
 *
 * @example
 * const isOnline = useOnlineStatus();
 *
 * if (!isOnline) {
 *   return <OfflineWarning />;
 * }
 */
export function useOnlineStatus() {
  const { useState } = require('react');
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useWindowEvent('online', () => setIsOnline(true));
  useWindowEvent('offline', () => setIsOnline(false));

  return isOnline;
}

/**
 * WHAT: Invokes callbacks when the browser tab becomes visible or hidden.
 * WHY:  DAMA uses auto-refresh intervals for campaign data; pausing them when
 *       the tab is hidden saves network traffic and avoids stale-data races.
 *
 * useVisibilityChange - Hook that tracks page visibility
 *
 * @param {Function} onVisible - Callback when page becomes visible
 * @param {Function} onHidden - Callback when page becomes hidden
 *
 * @example
 * // Pause/resume auto-refresh based on visibility
 * useVisibilityChange(
 *   () => startAutoRefresh(),
 *   () => stopAutoRefresh()
 * );
 */
export function useVisibilityChange(onVisible, onHidden) {
  const onVisibleRef = useRef(onVisible);
  const onHiddenRef = useRef(onHidden);

  useEffect(() => {
    onVisibleRef.current = onVisible;
    onHiddenRef.current = onHidden;
  }, [onVisible, onHidden]);

  useDocumentEvent('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onVisibleRef.current?.();
    } else {
      onHiddenRef.current?.();
    }
  });
}

export default useEventListener;
